// BrandAutocomplete — 7-OH brand picker for the dose-log sheet
// (PR 3B, Gap 6).
//
// Reads /api/brands once, surfaces a typeable autocomplete with the
// matching brand+label rows. Picking a row sets the brand ID; the
// LogDoseSheet then submits brandId with the dose POST, and the server
// substitutes the tested band for the label mg in computed_mg_7oh.
//
// Mobile discipline:
//  - Below the input, the matched-row list anchors directly under so
//    it doesn't get hidden by the virtual keyboard. Max 6 visible rows;
//    scroll inside the list if longer.
//  - Each row is ≥44px tall.
//  - Sub-label ("≈ 8–13mg real 7-OH") gives the user a peek at what
//    they're committing to before tapping.

import { useEffect, useMemo, useState } from 'react';
import { apiGet } from '../../lib/api';
import { KbLink } from './KbLink';

export interface BrandRow {
  id: number;
  brand: string;
  productLabel: string;
  advertisedMg: number;
  testedMgLow: number;
  testedMgHigh: number;
  confidencePct: number;
  sourceUrl: string;
}

interface BrandAutocompleteProps {
  /** Currently-selected brand id, or null. */
  value: number | null;
  onChange: (id: number | null, row: BrandRow | null) => void;
}

export function BrandAutocomplete({ value, onChange }: BrandAutocompleteProps) {
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [query, setQuery] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    void apiGet<{ brands: BrandRow[] }>('/api/brands')
      .then((r) => {
        setBrands(r.brands ?? []);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const selected = useMemo(
    () => brands.find((b) => b.id === value) ?? null,
    [brands, value],
  );

  // Reset the query when a selection is made; the input shows the
  // resolved brand+label as static text.
  const showInput = selected === null;

  const matches = useMemo(() => {
    if (!query.trim()) return brands.slice(0, 6);
    const q = query.trim().toLowerCase();
    return brands
      .filter(
        (b) =>
          b.brand.toLowerCase().includes(q) ||
          b.productLabel.toLowerCase().includes(q),
      )
      .slice(0, 6);
  }, [brands, query]);

  if (!loaded) {
    return (
      <div className="brand-autocomplete brand-autocomplete-loading">
        <span className="dim text-caption">Loading brands…</span>
      </div>
    );
  }

  if (!showInput && selected) {
    return (
      <div className="brand-autocomplete brand-autocomplete-selected">
        <div className="brand-autocomplete-selected-row">
          <div>
            <span className="brand-autocomplete-brand">{selected.brand}</span>
            <span className="brand-autocomplete-label"> · {selected.productLabel}</span>
          </div>
          <button
            type="button"
            className="brand-autocomplete-change"
            onClick={() => onChange(null, null)}
          >
            Change
          </button>
        </div>
        <p className="brand-autocomplete-band dim text-caption">
          Adjusted: ≈ {selected.testedMgLow}–{selected.testedMgHigh}mg real 7-OH
          (label says {selected.advertisedMg}mg)
        </p>
      </div>
    );
  }

  return (
    <div className="brand-autocomplete">
      <input
        type="text"
        className="brand-autocomplete-input"
        placeholder="Search brand (e.g. 7Tabz)"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoCapitalize="words"
        autoCorrect="off"
      />
      <ul className="brand-autocomplete-list">
        {matches.length === 0 ? (
          <li className="brand-autocomplete-empty dim text-caption">
            No brand matches that search. Leave it unselected if your brand
            isn't listed — the dose logs at the package mg.
          </li>
        ) : (
          matches.map((b) => (
            <li key={b.id}>
              <button
                type="button"
                className="brand-autocomplete-option"
                onClick={() => {
                  onChange(b.id, b);
                  setQuery('');
                }}
              >
                <div>
                  <span className="brand-autocomplete-brand">{b.brand}</span>
                  <span className="brand-autocomplete-label"> · {b.productLabel}</span>
                </div>
                <span className="brand-autocomplete-band-small dim text-caption">
                  ≈ {b.testedMgLow}–{b.testedMgHigh}mg
                </span>
              </button>
            </li>
          ))
        )}
      </ul>
      <p className="brand-autocomplete-helper dim text-caption">
        No brand selected logs the dose at the package mg — no lab-tested
        adjustment is applied.
      </p>
      <p className="brand-autocomplete-helper dim text-caption">
        <KbLink slug="advertised-vs-tested-mg">Why advertised mg ≠ tested mg →</KbLink>
      </p>
    </div>
  );
}
